import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Badge, Btn, Advanced } from "./common";
import { Plus, Trash2, Star, Briefcase, User, Mail, Link2 } from "lucide-react";
import { toast } from "sonner";

const MAIL_REASONS = {
  connected: ["success", "Ek posta hesabı bağlandı."],
  denied: ["error", "Google izin ekranında onay verilmedi."],
  already_primary: ["error", "Bu posta kutusu zaten birincil hesap olarak bağlı."],
  scope_too_wide: ["error", "Yalnızca okuma izni kabul edilir; verilen izin fazla olduğu için bağlanmadı."],
  scope_missing: ["error", "Okuma izni verilmedi; bağlanmadı."],
  state_invalid: ["error", "Güvenlik doğrulaması geçersiz; işlem iptal edildi."],
  state_expired: ["error", "Yetkilendirme süresi doldu; tekrar deneyin."],
  exchange_failed: ["error", "Google kod değişimi başarısız; tekrar deneyin."],
  identity_failed: ["error", "Hesap kimliği doğrulanamadı; bağlanmadı."],
  not_configured: ["error", "Sunucuda Google yapılandırması eksik."],
  bad_request: ["error", "Geri dönüş isteği eksik/bozuk."],
  ms_connected: ["success", "Outlook hesabı bağlandı."],
  ms_denied: ["error", "Microsoft izin ekranında onay verilmedi."],
  ms_state_invalid: ["error", "Güvenlik doğrulaması geçersiz; işlem iptal edildi."],
  ms_state_expired: ["error", "Yetkilendirme süresi doldu; tekrar deneyin."],
  ms_exchange_failed: ["error", "Microsoft kod değişimi başarısız; tekrar deneyin."],
  ms_scope_too_wide: ["error", "Verilen Microsoft izni fazla geniş olduğu için bağlanmadı."],
  ms_scope_missing: ["error", "Okuma izni verilmedi; bağlanmadı."],
  ms_identity_failed: ["error", "Microsoft hesabı doğrulanamadı; bağlanmadı."],
  ms_wrong_account: ["error", "Farklı bir Microsoft hesabıyla giriş yapıldı; izin güncellenmedi."],
  ms_not_configured: ["error", "Sunucuda Microsoft yapılandırması eksik."],
  ms_bad_request: ["error", "Geri dönüş isteği eksik/bozuk."],
};

/* Multi-account mail registry. The primary ModiHome mailbox (Gmail card above) is listed here too so its
   alias / role / default sender can be set. Extra mailboxes are read-only and never grant app login. */
export const MailAccountsCard = () => {
  const { tx } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/mail/accounts"); setData(r.data); }
    catch (e) { setData({ error: true, accounts: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("mail");
    if (!p) return;
    const [kind, msg] = MAIL_REASONS[p] || ["error", tx("Bağlantı tamamlanmadı ({p}).", { p })];
    toast[kind === "success" ? "success" : "error"](tx(msg));
    window.history.replaceState({}, "", window.location.pathname);
    load();
  }, [load, tx]);

  const connect = async (provider, mode = "read", key = "") => {
    setBusy(true);
    try {
      const r = await api.post("/mail/accounts/connect", null, { params: { provider, mode, key } });
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || tx("Bağlantı başlatılamadı"));
      setBusy(false);
    }
  };
  const update = async (key, patch) => {
    try { const r = await api.put(`/mail/accounts/${key}`, patch); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || tx("Güncellenemedi")); }
  };
  const remove = async (key) => {
    if (!window.confirm(tx("Bu ek posta hesabı kaldırılsın mı? Google'daki okuma izni de iptal edilir."))) return;
    try { await api.delete(`/mail/accounts/${key}`); toast.success(tx("Ek hesap kaldırıldı")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || tx("Kaldırılamadı")); }
  };

  if (!data) return null;
  const accounts = data.accounts || [];
  const ms = data.microsoft || {};

  return (
    <div data-testid="mail-accounts-card">
      <p className="text-[0.72rem] text-white/55 mb-3">
        {tx("İş ve kişisel posta hesaplarını buradan yönetin. Ek hesap eklemek uygulamaya giriş yetkisi vermez; yalnızca sahibin kendisi Google izin ekranından onaylar. Varsayılan hesap, asistanın araç ve otomasyonlarda hedeflediği posta kutusudur.")}
      </p>

      {accounts.length === 0 && (
        <div className="text-sm text-white/45 mb-3" data-testid="mail-accounts-empty">
          {tx("Henüz bağlı posta hesabı yok. Yukarıdaki Gmail kartından birincil ModiHome kutusunu bağlayın.")}
        </div>
      )}

      <div className="space-y-2" data-testid="mail-accounts-list">
        {accounts.map((a) => (
          <div key={a.key} className="bg-black/20 rounded-lg px-3 py-2.5" data-testid={`mail-account-${a.key}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">{a.alias || a.email}</span>
                  {a.is_default && <Badge color="cyan" testid={`mail-default-${a.key}`}>{tx("varsayılan")}</Badge>}
                  {a.is_primary && <Badge color="green">{tx("birincil")}</Badge>}
                </div>
                <div className="label-eyebrow truncate">{a.email} · {a.provider}
                  {a.can_send ? ` · ${tx("gönderme açık")}` : ""}{a.can_modify ? ` · ${tx("değiştirme açık")}` : ""}</div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {!a.is_default && (
                  <Btn variant="ghost" onClick={() => update(a.key, { is_default: true })} data-testid={`mail-set-default-${a.key}`}>
                    <Star size={12} className="inline mr-1" />{tx("Varsayılan yap")}
                  </Btn>
                )}
                {!a.is_primary && (
                  <Btn variant="danger" onClick={() => remove(a.key)} aria-label={tx("Kaldır")} data-testid={`mail-remove-${a.key}`}>
                    <Trash2 size={12} />
                  </Btn>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="label-eyebrow">{tx("Rol:")}</span>
                {[["work", tx("İş"), Briefcase], ["personal", tx("Kişisel"), User]].map(([role, lbl, Icon]) => (
                  <button key={role} onClick={() => update(a.key, { role })} data-testid={`mail-role-${a.key}-${role}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[0.7rem] ${a.role === role ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-white/55 hover:text-cyan-200"}`}>
                    <Icon size={11} />{lbl}
                  </button>
                ))}
              </div>
              {a.provider === "microsoft" && (
                <div className="flex items-center gap-1.5" data-testid={`mail-ms-perms-${a.key}`}>
                  {!a.can_modify && <Btn variant="ghost" onClick={() => connect("microsoft", "modify", a.key)} disabled={busy}>{tx("Değiştirmeyi etkinleştir")}</Btn>}
                  {!a.can_send && <Btn variant="ghost" onClick={() => connect("microsoft", "send", a.key)} disabled={busy}>{tx("Göndermeyi etkinleştir")}</Btn>}
                </div>
              )}
              <label className="flex items-center gap-1.5">
                <span className="label-eyebrow">{tx("Takma ad:")}</span>
                <input defaultValue={a.alias || ""} data-testid={`mail-alias-${a.key}`}
                  onBlur={(e) => { if ((e.target.value || "") !== (a.alias || "")) update(a.key, { alias: e.target.value }); }}
                  className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-white w-32 focus:outline-none focus:border-cyan-400/50" />
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap mt-3">
        <Btn onClick={() => connect("google")} disabled={busy} data-testid="mail-add-google-btn">
          <Plus size={13} className="inline mr-1" />{tx("Google hesabı ekle")}
        </Btn>
      </div>
      {data.google?.expected_redirect_uri && (
        <div className="mt-2 bg-black/20 border border-amber-400/15 rounded-lg px-3 py-2.5" data-testid="mail-google-redirect-note">
          <p className="text-[0.7rem] text-amber-200/85">
            {tx("Google \"400 redirect_uri_mismatch\" alıyorsanız, Google Cloud Console → ilgili OAuth istemcisi → \"Authorized redirect URIs\" listesine aşağıdaki adresi bir kez ekleyin (ana Gmail bağlantısı etkilenmez):")}
          </p>
          <code className="block mt-1 text-[0.68rem] text-cyan-200 break-all" data-testid="mail-google-redirect-uri">{data.google.expected_redirect_uri}</code>
          <button onClick={() => { navigator.clipboard?.writeText(data.google.expected_redirect_uri); toast.success(tx("Kopyalandı")); }}
            className="mt-1 text-[0.66rem] text-cyan-300 hover:text-cyan-200" data-testid="mail-google-redirect-copy">{tx("Adresi kopyala")}</button>
        </div>
      )}

      {/* Microsoft / Outlook — honest pending, never shown as connected without real credentials */}
      <div className="mt-3 bg-black/20 border border-amber-400/15 rounded-lg px-3 py-2.5" data-testid="mail-microsoft-pending">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-white/80"><Mail size={14} />Microsoft / Outlook</div>
          <Badge color={ms.configured ? "green" : "amber"}>{ms.configured ? tx("hazır") : tx("bağlantı bekliyor")}</Badge>
        </div>
        <p className="text-[0.7rem] text-amber-200/80 mt-1.5" data-testid="mail-microsoft-summary">
          {ms.configured ? tx("Kişisel (Outlook.com/Hotmail) veya iş hesabı ekleyebilirsiniz; önce yalnızca okuma izni istenir.")
            : tx("Henüz bağlı değil — kurulum için Azure uygulama kaydı gerekir.")}
        </p>
        {ms.configured && (
          <Btn onClick={() => connect("microsoft")} disabled={busy} className="mt-2" data-testid="mail-add-microsoft-btn">
            <Plus size={13} className="inline mr-1" />{tx("Outlook hesabı ekle")}
          </Btn>
        )}
        {/* technical configuration (env names, redirect URI) stays collapsed */}
        <Advanced label={tx("Teknik yapılandırma")} testid="mail-microsoft-advanced">
          {ms.note && <div>{tx(ms.note)}</div>}
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(ms.env || {}).map(([k, v]) => (
              <Badge key={k} color={v ? "green" : "gray"} testid={`mail-ms-env-${k}`}>{k}: {v ? tx("var") : tx("yok")}</Badge>
            ))}
          </div>
        </Advanced>
      </div>

      <Advanced label={tx("Hesap ayrımı nasıl çalışır?")} testid="mail-accounts-advanced">
        <div>{tx("Birincil ModiHome kutusu Gmail kartındaki yetkilerle (okuma, gönderme, arşiv/çöp, takvim) çalışır; ek hesaplar yalnızca okunur ve asistana giriş hakkı vermez.")}</div>
        <div>{tx("Varsayılan hesap, \"mail var mı\" gibi sorularda ve yeni e-posta otomasyonlarında hedeflenir; başka hesabı kastederek sorarsanız (ör. \"kişisel hesabımda\") asistan ona yönelir.")}</div>
        <div>{tx("Microsoft/Outlook için Azure uygulama kaydı kimlik bilgileri eklenmeden bağlantı kurulmaz.")}</div>
      </Advanced>
    </div>
  );
};

export default MailAccountsCard;
